import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useAxios from "../../../utils/useAxios";
import AuthContext from "../../../context/AuthContext";
import { useMasterArtworkMyApprovalContextStore } from "../../../context/MasterArtworkMyApprovalsContext";
import { MyApprovalActions } from "../../../states/meta-artwork/my-approval-request";
import { isStateEqualToInitialState } from "../../../states/meta-artwork/my-approval-request/Reducer";
import { PageableTableProvider } from "context/PageableTableContext";
import { TableArtworkRequest } from "../components/ArtworkPagableTable";

// TODO: CUSTOM DEBOUNCE
export const useDebounce = (delay: number = 300) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [isLoading, setIsLoading] = useState(false);

  const debounce = useCallback(
    (
      fn: (...args: any[]) => void,
      input: any,
      defaultValue: any,
      callback: (value: any) => void,
    ) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setIsLoading(true);

      // If input is empty, execute immediately with empty value
      if (!input) {
        fn();
        setIsLoading(false);
        return;
      }

      // If input is less than minimum required length, return default value
      if (input.length < 3) {
        callback(defaultValue);
        setIsLoading(false);
        return;
      }

      timeoutRef.current = setTimeout(async () => {
        try {
          // Call the provided function and pass result to callback
          const result = await fn(input);
          callback(result);
        } catch (error) {
          console.error("Debounced function error:", error);
          callback(defaultValue);
        } finally {
          setIsLoading(false);
        }
      }, delay);
    },
    [delay],
  );

  return { debounce, isLoading };
};

export const ArtworkMasterPage = (props: { title?: string }) => {
  const axios = useAxios();
  const authContext = useContext(AuthContext);
  const username = authContext?.username;

  const {
    state: { dataResults, ...searchParams },
    dispatch,
  } = useMasterArtworkMyApprovalContextStore();

  const { debounce: debouncedSearch } = useDebounce(1000); // 300ms delay

  // Create a memoized handler for search updates
  const handleChangeSearchField = useCallback(
    (e: any) => {
      debouncedSearch(
        () => {
          dispatch(
            MyApprovalActions.setFilterApplied(
              isStateEqualToInitialState({ dataResults, ...searchParams }),
            ),
          );
          dispatch(MyApprovalActions.setSearch(e.target.value));
        },
        e.target.value,
        searchParams.search,
        () => {}, // empty callback since we're not using the result
      );
    },
    [dataResults, debouncedSearch, dispatch, searchParams],
  );

  // Similarly for the requestor field
  const handleChangeRequestor = useCallback(
    (e: any) => {
      debouncedSearch(
        () => {
          dispatch(
            MyApprovalActions.setFilterApplied(
              isStateEqualToInitialState({ dataResults, ...searchParams }),
            ),
          );
          dispatch(MyApprovalActions.setRequestor(e.target.value));
        },
        e.target.value,
        searchParams.requestor,
        () => {}, // empty callback since we're not using the result
      );
    },
    [dataResults, debouncedSearch, dispatch, searchParams],
  );

  // Keep immediate updates for non-text inputs
  const handleChangeStatusFilter = useCallback(
    (e: any) => {
      const newStatus = e.target.checked
        ? [...searchParams.status, e.target.name]
        : searchParams.status.filter((status: any) => status !== e.target.name);

      // First update the filter applied state
      dispatch(
        MyApprovalActions.setFilterApplied(
          isStateEqualToInitialState({
            dataResults,
            ...searchParams,
            status: newStatus,
          }),
        ),
      );

      // Then immediately update the status
      dispatch(MyApprovalActions.setStatus(newStatus));
    },
    [dataResults, dispatch, searchParams],
  );

  const handleChangeLastDays = (e: any) => {
    dispatch(
      MyApprovalActions.setFilterApplied(
        isStateEqualToInitialState({ dataResults, ...searchParams }),
      ),
    );
    dispatch(MyApprovalActions.setLastDays(e.target.value));
  };

  const handleChangeLimit = (e: any) => {
    dispatch(
      MyApprovalActions.setFilterApplied(
        isStateEqualToInitialState({ dataResults, ...searchParams }),
      ),
    );
    dispatch(MyApprovalActions.setLimit(e.target.value));
  };

  // @ts-ignore
  return (
    <div
      className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
      style={{ minHeight: "70vh" }}
    >
      <h6 className="text-primary fw-bold card-header border-0 mb-2">
        Approval Requests
      </h6>

      <>
        <div className="d-flex justify-content-between table-top-container">
          <div className="d-flex align-items-center col-md-4">
            <label className="d-inline-block">
              Show
              <select
                onChange={(e) => handleChangeLimit(e)}
                className="table-top-input"
                id="page-size"
                value={searchParams.limit}
              >
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="-1">All</option>
              </select>
              entries
            </label>
          </div>

          <div className="d-flex align-items-center col-md-4">
            <label className="d-inline-block">
              Show requests from last
              <select
                onChange={(e) => handleChangeLastDays(e)}
                className="table-top-input"
                id="page-size"
                value={searchParams.lastDays}
              >
                <option value="-1">All</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
              days
            </label>
          </div>

          <div className="d-flex justify-content-end align-items-center col-md-4 flex-fill">
            <div
              id="pp_search_filter"
              className="dataTables_filter table-top-search d-flex align-items-center w-100"
            >
              <label className={"mb-0"}>Search</label>
              <input
                onChange={(e) => handleChangeSearchField(e)}
                type="search"
                className="table-top-input flex-fill"
                placeholder=""
                aria-controls="pp_search"
              />
            </div>
          </div>
        </div>

        <div className="d-flex table-top-container justify-content-between">
          <div className="d-flex justify-content-center align-items-center offset-md-3 col-md-4">
            <div
              id="pp_search_filter"
              className="dataTables_filter table-top-search"
            >
              <label>
                Requestor
                <input
                  onChange={(e) => handleChangeRequestor(e)}
                  type="search"
                  className="table-top-input"
                  placeholder=""
                  aria-controls="pp_search"
                  // value={search}
                  defaultChecked={true}
                />
              </label>
            </div>
          </div>

          <div className="d-flex justify-content-end align-items-center col-md-4">
            <div
              id="pp_search_filter"
              className="dataTables_filter table-top-search"
            >
              <input
                onChange={(e) => {
                  handleChangeStatusFilter(e);
                }}
                name="APPROVED"
                id="search_filter_approved"
                type="checkbox"
                className="table-top-input"
                placeholder=""
                aria-controls="pp_search"
                // value={search}
                defaultChecked={searchParams.status.includes("APPROVED")}
              />
              <label htmlFor="search_filter_approved">Approved</label>
            </div>
            <div
              id="pp_search_filter"
              className="dataTables_filter table-top-search"
            >
              <input
                onChange={(e) => {
                  handleChangeStatusFilter(e);
                }}
                name="CREATED"
                id="search_filter_created"
                type="checkbox"
                className="table-top-input"
                placeholder=""
                aria-controls="pp_search"
                defaultChecked={searchParams.status.includes("CREATED")}
              />
              <label htmlFor="search_filter_created">Created</label>
            </div>
            <div
              id="pp_search_filter"
              className="dataTables_filter table-top-search"
            >
              <input
                onChange={(e) => {
                  handleChangeStatusFilter(e);
                }}
                name="SUBMITTED"
                id="search_filter_submitted"
                type="checkbox"
                className="table-top-input"
                placeholder=""
                aria-controls="pp_search"
                defaultChecked={searchParams.status.includes("SUBMITTED")}
              />
              <label htmlFor="search_filter_submitted">Submitted</label>
            </div>
            <div
              id="pp_search_filter"
              className="dataTables_filter table-top-search"
            >
              <input
                onChange={(e) => {
                  handleChangeStatusFilter(e);
                }}
                id="search_filter_rejected"
                name="REJECTED"
                type="checkbox"
                className="table-top-input"
                placeholder=""
                aria-controls="pp_search"
                defaultChecked={searchParams.status.includes("REJECTED")}
              />
              <label htmlFor="search_filter_rejected">Rejected</label>
            </div>
          </div>
        </div>

        <PageableTableProvider>
          <TableArtworkRequest />
        </PageableTableProvider>
      </>
    </div>
  );
};
